import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { motion, AnimatePresence } from "framer-motion";

interface TemplateType {
  type_code: string;
  name: string;
}

interface ReportPreviewModalProps {
  show: boolean;
  isDarkMode: boolean;
  inspectionId: number;
  vehicleNumber: string;
  reportUrl: string;
  onClose: () => void;
}

export const ReportPreviewModal: React.FC<ReportPreviewModalProps> = ({
  show,
  isDarkMode,
  inspectionId,
  vehicleNumber,
  reportUrl,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [templateTypes, setTemplateTypes] = useState<TemplateType[]>([]);
  const [selectedTab, setSelectedTab] = useState<string>("");
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Fetch available template types
  useEffect(() => {
    if (show && inspectionId) {
      const fetchTemplateTypes = async () => {
        try {
          setLoadingTypes(true);
          const API_BASE_URL =
            process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
          const url = `${API_BASE_URL}/inspections/${inspectionId}/report/template-types`;

          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          });

          if (response.ok) {
            const data = await response.json();

            setTemplateTypes(data.template_types || []);

            // Set first template as default
            if (data.template_types && data.template_types.length > 0) {
              const firstType = data.template_types[0].type_code;
              setSelectedTab(firstType);
            }
          } else {
            console.error(
              "[ReportModal] Failed to fetch template types. Status:",
              response.status
            );
            const errorText = await response.text();
            console.error("[ReportModal] Error response:", errorText);
          }
        } catch (error) {
          console.error("[ReportModal] Error fetching template types:", error);
        } finally {
          setLoadingTypes(false);
        }
      };

      fetchTemplateTypes();
    }
  }, [show, inspectionId]);

  // Reset loading state when tab changes
  useEffect(() => {
    if (selectedTab) {
      setIsLoading(true);
      setHasError(false);
    }
  }, [selectedTab]);

  const getCurrentReportUrl = () => {
    if (!selectedTab) return reportUrl;
    const baseUrl = reportUrl.split("?")[0];
    return `${baseUrl}?template_type=${selectedTab}`;
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);

      // Fetch with authentication - use current tab's report URL
      const currentUrl = getCurrentReportUrl();
      const response = await fetch(currentUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download report");
      }

      // Get the blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const templateSuffix = selectedTab ? `_${selectedTab}` : "";
      link.download = `inspection_report_${vehicleNumber}_${inspectionId}${templateSuffix}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar("Report downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading report:", error);
      enqueueSnackbar("Error downloading report. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-6xl h-full max-h-[90vh] rounded-xl shadow-2xl overflow-hidden flex flex-col ${
              isDarkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-phoenix-600 to-phoenix-700 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Inspection Report
                  </h3>
                  <p className="text-phoenix-100 text-sm">
                    Vehicle #{vehicleNumber}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Download Button */}
                <motion.button
                  whileHover={{ scale: isDownloading ? 1 : 1.05 }}
                  whileTap={{ scale: isDownloading ? 1 : 0.95 }}
                  onClick={handleDownload}
                  disabled={isDownloading}
                  className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white font-semibold transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      Download
                    </>
                  )}
                </motion.button>

                {/* Close Button */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.button>
              </div>
            </div>

            {/* Tabs for different report types */}
            {templateTypes.length > 0 && (
              <div
                className={`px-6 py-3 border-b ${
                  isDarkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex gap-2 overflow-x-auto">
                  {loadingTypes ? (
                    <div className="flex items-center gap-2 py-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-phoenix-600" />
                      <span
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Loading report types...
                      </span>
                    </div>
                  ) : (
                    <>
                      <span
                        className={`text-xs py-2 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        Report Types ({templateTypes.length}):
                      </span>
                      {templateTypes.map((template) => (
                        <motion.button
                          key={template.type_code}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedTab(template.type_code);
                          }}
                          className={`px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap transition-all ${
                            selectedTab === template.type_code
                              ? "bg-phoenix-600 text-white shadow-md"
                              : isDarkMode
                              ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {template.name}
                        </motion.button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* PDF Preview */}
            <div className="flex-1 relative overflow-hidden">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-phoenix-600 mx-auto mb-4" />
                    <p
                      className={`text-lg font-semibold ${
                        isDarkMode ? "text-gray-300" : "text-gray-600"
                      }`}
                    >
                      Loading report...
                    </p>
                  </div>
                </div>
              )}

              {hasError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-10 h-10 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p
                      className={`text-lg font-semibold mb-2 ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Error loading report
                    </p>
                    <p
                      className={`text-sm mb-4 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      The report could not be loaded. Please try downloading it
                      instead.
                    </p>
                    <button
                      onClick={handleDownload}
                      className="px-6 py-2 rounded-lg bg-phoenix-600 hover:bg-phoenix-700 text-white font-semibold transition-colors"
                    >
                      Download Report
                    </button>
                  </div>
                </div>
              )}

              <iframe
                key={selectedTab} // Force re-render when tab changes
                src={`${getCurrentReportUrl()}#toolbar=1&navpanes=0&scrollbar=1`}
                className="w-full h-full border-0"
                title="Inspection Report"
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setHasError(true);
                }}
              />
            </div>

            {/* Footer Info */}
            <div
              className={`px-6 py-3 text-sm flex items-center justify-between ${
                isDarkMode
                  ? "bg-gray-900/50 text-gray-400"
                  : "bg-gray-50 text-gray-600"
              }`}
            >
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  Use the toolbar above to zoom, print, or download the report
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                {selectedTab && (
                  <span className="px-2 py-1 rounded bg-phoenix-600/20 text-phoenix-600 font-semibold">
                    {templateTypes.find((t) => t.type_code === selectedTab)
                      ?.name || selectedTab}
                  </span>
                )}
                <span>Inspection ID: {inspectionId}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
